/**
 * BaselineManager - Gestion de Lineas Base de Requisitos
 * PECAL 2110 - Control de Configuracion
 */

import { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Layers,
  Plus,
  MoreVertical,
  Eye,
  CheckCircle,
  Trash2,
  GitCompare,
  Lock,
  Calendar,
  User,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  useBaselines,
  useRequirements,
  useCreateBaseline,
  useApproveBaseline,
  useDeleteBaseline,
  useCompareBaselines,
} from '@/hooks/useRequirements';
import {
  RequirementBaseline,
  BaselineType,
  BaselineStatus,
  baselineTypeLabels,
  baselineStatusLabels,
  baselineStatusColors,
  baselineTypeColors,
  Requirement,
} from '@/types/requirements';

interface BaselineManagerProps {
  onViewBaseline?: (baseline: RequirementBaseline) => void;
  className?: string;
}

export function BaselineManager({ onViewBaseline, className }: BaselineManagerProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [selectedBaseline, setSelectedBaseline] = useState<RequirementBaseline | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const { data: baselines = [], isLoading } = useBaselines({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
  });

  const handleApprove = (baseline: RequirementBaseline) => {
    setSelectedBaseline(baseline);
    setShowApproveDialog(true);
  };

  const handleDelete = (baseline: RequirementBaseline) => {
    setSelectedBaseline(baseline);
    setShowDeleteDialog(true);
  };

  const handleCompare = (baseline: RequirementBaseline) => {
    setSelectedBaseline(baseline);
    setShowCompareDialog(true);
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Lineas Base
            </CardTitle>
            <CardDescription>
              Control de configuracion segun PECAL 2110
            </CardDescription>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Linea Base
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex gap-4 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="under_review">En Revision</SelectItem>
              <SelectItem value="frozen">Congelada</SelectItem>
              <SelectItem value="superseded">Reemplazada</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="functional">Funcional</SelectItem>
              <SelectItem value="allocated">Asignada</SelectItem>
              <SelectItem value="product">Producto</SelectItem>
              <SelectItem value="development">Desarrollo</SelectItem>
              <SelectItem value="release">Release</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Cargando...
          </div>
        ) : baselines.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No hay lineas base</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codigo</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-center">Requisitos</TableHead>
                <TableHead className="text-center">Cobertura</TableHead>
                <TableHead>Creada</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {baselines.map((baseline) => (
                <TableRow key={baseline.id}>
                  <TableCell className="font-mono text-sm">
                    {baseline.baseline_id}
                  </TableCell>
                  <TableCell className="font-medium">{baseline.name}</TableCell>
                  <TableCell>
                    <Badge className={cn(baselineTypeColors[baseline.baseline_type])}>
                      {baselineTypeLabels[baseline.baseline_type]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn(baselineStatusColors[baseline.status])}>
                      {baseline.status === 'frozen' && <Lock className="w-3 h-3 mr-1" />}
                      {baselineStatusLabels[baseline.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {baseline.total_requirements}
                  </TableCell>
                  <TableCell className="text-center">
                    <span
                      className={cn(
                        'font-semibold',
                        (baseline.coverage_percentage || 0) >= 80
                          ? 'text-green-600'
                          : (baseline.coverage_percentage || 0) >= 50
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      )}
                    >
                      {baseline.coverage_percentage?.toFixed(0) || 0}%
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(baseline.created_at), 'dd MMM yyyy', { locale: es })}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewBaseline?.(baseline)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Detalle
                        </DropdownMenuItem>
                        {baseline.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleApprove(baseline)}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprobar y Congelar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleCompare(baseline)}>
                          <GitCompare className="w-4 h-4 mr-2" />
                          Comparar
                        </DropdownMenuItem>
                        {baseline.status === 'draft' && (
                          <DropdownMenuItem
                            onClick={() => handleDelete(baseline)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <CreateBaselineDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      <ApproveBaselineDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        baseline={selectedBaseline}
      />

      <DeleteBaselineDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        baseline={selectedBaseline}
      />

      <CompareBaselinesDialog
        open={showCompareDialog}
        onOpenChange={setShowCompareDialog}
        baseline={selectedBaseline}
        allBaselines={baselines}
      />
    </Card>
  );
}

function CreateBaselineDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baselineType, setBaselineType] = useState<BaselineType>('functional');
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const { data: requirements = [] } = useRequirements({
    status: 'approved',
  });
  const createBaseline = useCreateBaseline();

  const filteredRequirements = useMemo(() => {
    if (!search) return requirements;
    const searchLower = search.toLowerCase();
    return requirements.filter(
      (r) =>
        r.requirement_id.toLowerCase().includes(searchLower) ||
        r.title.toLowerCase().includes(searchLower)
    );
  }, [requirements, search]);

  const handleToggleRequirement = (id: string) => {
    setSelectedRequirements((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredRequirements.map((r) => r.id);
    setSelectedRequirements(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedRequirements([]);
  };

  const handleSubmit = async () => {
    if (!name || selectedRequirements.length === 0) return;

    await createBaseline.mutateAsync({
      name,
      description: description || undefined,
      baseline_type: baselineType,
      requirement_ids: selectedRequirements,
    });

    setName('');
    setDescription('');
    setBaselineType('functional');
    setSelectedRequirements([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Nueva Linea Base
          </DialogTitle>
          <DialogDescription>
            Crea una nueva linea base de requisitos (PECAL 2110)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Baseline Funcional v1.0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo de Linea Base *</Label>
              <Select
                value={baselineType}
                onValueChange={(v: BaselineType) => setBaselineType(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="functional">Funcional</SelectItem>
                  <SelectItem value="allocated">Asignada</SelectItem>
                  <SelectItem value="product">Producto</SelectItem>
                  <SelectItem value="development">Desarrollo</SelectItem>
                  <SelectItem value="release">Release</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripcion</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripcion de la linea base..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Requisitos Aprobados ({selectedRequirements.length} seleccionados)</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  Seleccionar Todo
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                  Deseleccionar
                </Button>
              </div>
            </div>

            <Input
              placeholder="Buscar requisitos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2"
            />

            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {filteredRequirements.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No hay requisitos aprobados disponibles
                </div>
              ) : (
                filteredRequirements.map((req) => (
                  <div
                    key={req.id}
                    className={cn(
                      'flex items-center gap-3 p-2 border-b last:border-0 hover:bg-muted/50 cursor-pointer',
                      selectedRequirements.includes(req.id) && 'bg-primary/5'
                    )}
                    onClick={() => handleToggleRequirement(req.id)}
                  >
                    <Checkbox
                      checked={selectedRequirements.includes(req.id)}
                      onCheckedChange={() => handleToggleRequirement(req.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {req.requirement_id}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {req.level.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{req.title}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name || selectedRequirements.length === 0 || createBaseline.isPending}
          >
            {createBaseline.isPending ? 'Creando...' : 'Crear Linea Base'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveBaselineDialog({
  open,
  onOpenChange,
  baseline,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseline: RequirementBaseline | null;
}) {
  const [notes, setNotes] = useState('');
  const approveBaseline = useApproveBaseline();

  const handleApprove = async () => {
    if (!baseline) return;

    await approveBaseline.mutateAsync({
      id: baseline.id,
      notes: notes || undefined,
    });

    setNotes('');
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Aprobar y Congelar Linea Base
          </AlertDialogTitle>
          <AlertDialogDescription>
            Esta accion congelara la linea base <strong>{baseline?.baseline_id}</strong>.
            <br />
            Una vez congelada, no podra ser modificada (PECAL 2110).
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <Label>Notas de Aprobacion</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas opcionales..."
            rows={2}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleApprove}
            disabled={approveBaseline.isPending}
            className="bg-green-600 hover:bg-green-700"
          >
            {approveBaseline.isPending ? 'Aprobando...' : 'Aprobar y Congelar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteBaselineDialog({
  open,
  onOpenChange,
  baseline,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseline: RequirementBaseline | null;
}) {
  const deleteBaseline = useDeleteBaseline();

  const handleDelete = async () => {
    if (!baseline) return;
    await deleteBaseline.mutateAsync(baseline.id);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar Linea Base</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Esta seguro de eliminar la linea base <strong>{baseline?.baseline_id}</strong>?
            <br />
            Esta accion no se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteBaseline.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleteBaseline.isPending ? 'Eliminando...' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CompareBaselinesDialog({
  open,
  onOpenChange,
  baseline,
  allBaselines,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseline: RequirementBaseline | null;
  allBaselines: RequirementBaseline[];
}) {
  const [compareWith, setCompareWith] = useState<string>('');

  const { data: comparison = [], isLoading } = useCompareBaselines(
    baseline?.id || null,
    compareWith || null
  );

  const otherBaselines = useMemo(
    () => allBaselines.filter((b) => b.id !== baseline?.id),
    [allBaselines, baseline]
  );

  const stats = useMemo(() => {
    const added = comparison.filter((c) => c.comparison_type === 'added').length;
    const removed = comparison.filter((c) => c.comparison_type === 'removed').length;
    const modified = comparison.filter((c) => c.comparison_type === 'modified').length;
    return { added, removed, modified, total: comparison.length };
  }, [comparison]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-5 h-5" />
            Comparar Lineas Base
          </DialogTitle>
          <DialogDescription>
            Comparando {baseline?.baseline_id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Comparar con</Label>
            <Select value={compareWith} onValueChange={setCompareWith}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar linea base..." />
              </SelectTrigger>
              <SelectContent>
                {otherBaselines.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.baseline_id} - {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {compareWith && (
            <>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Comparando...
                </div>
              ) : comparison.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  Las lineas base son identicas
                </div>
              ) : (
                <>
                  <div className="flex gap-4">
                    <Badge variant="outline" className="bg-green-50">
                      +{stats.added} agregados
                    </Badge>
                    <Badge variant="outline" className="bg-red-50">
                      -{stats.removed} eliminados
                    </Badge>
                    <Badge variant="outline" className="bg-yellow-50">
                      ~{stats.modified} modificados
                    </Badge>
                  </div>

                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {comparison.map((item, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'p-3 border-b last:border-0 flex items-center gap-3',
                          item.comparison_type === 'added' && 'bg-green-50',
                          item.comparison_type === 'removed' && 'bg-red-50',
                          item.comparison_type === 'modified' && 'bg-yellow-50'
                        )}
                      >
                        <span
                          className={cn(
                            'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white',
                            item.comparison_type === 'added' && 'bg-green-600',
                            item.comparison_type === 'removed' && 'bg-red-600',
                            item.comparison_type === 'modified' && 'bg-yellow-600'
                          )}
                        >
                          {item.comparison_type === 'added'
                            ? '+'
                            : item.comparison_type === 'removed'
                            ? '-'
                            : '~'}
                        </span>
                        <div className="flex-1">
                          <span className="font-mono text-sm font-medium">
                            {item.requirement_code}
                          </span>
                          <p className="text-sm text-muted-foreground">
                            {item.change_description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
